import { ChevronsUpDown, User, Users } from 'lucide-react';
import { useCallback, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button.tsx';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.tsx';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { cn } from '../../../lib/utils.ts';
import { useViewMode, type ViewMode } from '../hooks/use-view-mode.ts';

export type ViewModeSwitchProps = {
    /** Expanded renders a labelled dropdown; collapsed renders a single toggle icon. */
    expanded?: boolean;
};

/**
 * Lets a manager switch between the Manager cockpit layout and the Rep
 * layout. It's an infrequent switch, so it's a low-weight dropdown
 * (showing the current view) rather than a prominent segmented control.
 * Renders nothing unless the manager experience is active (flag on + manager
 * role), so it is safe to mount unconditionally in every sidebar shell.
 */
export const ViewModeSwitch: FC<ViewModeSwitchProps> = ({ expanded = true }) => {
    const { t } = useLocale('common');
    const navigate = useNavigate();
    const { viewMode, setViewMode } = useViewMode();

    // An explicit toggle is a deliberate context switch: change the mode and
    // always land the user on home, rather than leaving them on a page that may
    // not belong to the new view. (The page guard handles the separate case of
    // a link opened directly in the wrong view — switch + stay + toast.)
    const switchViewMode = useCallback(
        (next: ViewMode) => {
            setViewMode(next);
            void navigate('/', { replace: true });
        },
        [setViewMode, navigate],
    );

    const currentLabel = viewMode === 'MANAGER' ? t('view_switch__manager_view') : t('view_switch__rep_view');

    if (!expanded) {
        const nextMode: ViewMode = viewMode === 'MANAGER' ? 'REP' : 'MANAGER';
        const tooltip =
            viewMode === 'MANAGER' ? t('view_switch__switch_to_rep') : t('view_switch__switch_to_manager');

        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        size="icon-s"
                        variant="ghost"
                        aria-label={tooltip}
                        icon={viewMode === 'MANAGER' ? Users : User}
                        onClick={() => {
                            switchViewMode(nextMode);
                        }}
                    />
                </TooltipTrigger>
                <TooltipContent side="right">{tooltip}</TooltipContent>
            </Tooltip>
        );
    }

    return (
        <DropdownMenu modal>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'flex h-8 w-full items-center justify-between rounded-md px-3',
                        'text-sm font-medium text-neutral-700 hover:bg-neutral-100',
                        'aria-expanded:bg-neutral-100',
                    )}
                >
                    <span>{currentLabel}</span>
                    <ChevronsUpDown className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden="true" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                className="pointer-events-auto w-[var(--radix-dropdown-menu-trigger-width)]"
            >
                <DropdownMenuItem
                    onSelect={() => {
                        switchViewMode('MANAGER');
                    }}
                >
                    {t('view_switch__manager_view')}
                </DropdownMenuItem>
                <DropdownMenuItem
                    onSelect={() => {
                        switchViewMode('REP');
                    }}
                >
                    {t('view_switch__rep_view')}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
